import { css } from '@emotion/react';
import { Formik } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { usePageTitle, usePersistedSearch, usePersistedSort } from '../../Lib';

import { NoRecordsPlaceholder, RecordListHeading } from '@/Client/Partials';
import { ApiOrgContractor } from '@kaiizen/kai-pro-api';
import { useAllOrgSettings, useAuth } from '@kaiizen/kai-pro-api-react-state';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import searchIcon from '../../Assets/search-v2.svg';
import { PageLayout } from '../../Layout';
import { GenericLoadingPage } from '../../Pages/GenericPages';
import { useAutoSwitchOrgForResource, useOrgContractor, useOrgContractors, useOrgMarkets, useToast } from '../../State';
import { LAYOUT2 } from '../../Styles';
import { Button, DataTable, FlyOut, FormFieldsRequirementNote, FormikLabeledInput, FormikLabeledMultiSelect, StyledForm } from '../../Widgets';
import { INPUTS_TRADE_SEEDS } from '../OrgSettings/InputsTradeGroupsData';

const SEARCH_BAR_STYLE = css`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  border: 1px solid #cfcfcf;
  height: 42px;
  width: 360px;
  padding: 0 10px;
`;

const SEARCH_ICON_STYLE = css`
  width: 18px;
  height: 18px;
  opacity: 0.45;
  filter: brightness(0) saturate(100%);
`;

const SEARCH_COLLAPSED_BUTTON_STYLE = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  background: #ffffff;
  border: 1px solid #cfcfcf;
  cursor: pointer;
  padding: 0;
`;

const SEARCH_ICON_BUTTON_STYLE = css`
  background: transparent;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const SEARCH_INPUT_STYLE = css`
  flex: 1;
  background: transparent;
  border: none;
  color: #1f1f1f;
  outline: none;

  &::placeholder {
    color: #767676;
  }

  &::-webkit-search-cancel-button {
    -webkit-appearance: none;
    appearance: none;
    display: none;
  }
`;

const SEARCH_CLOSE_BUTTON_STYLE = css`
  position: relative;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 18px;
  height: 18px;
  flex-shrink: 0;

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 8px;
    top: 1px;
    width: 2px;
    height: 16px;
    background: #1f1f1f;
    border-radius: 1px;
  }

  &::before {
    transform: rotate(45deg);
  }

  &::after {
    transform: rotate(-45deg);
  }
`;

const getTradeLabel = (
  trade: string,
  tradeDisplayNames: Record<string, { displayName?: string }>,
) => tradeDisplayNames[trade]?.displayName || trade;

const DEFAULT_TRADE_DISPLAY_NAMES = Object.fromEntries(
  INPUTS_TRADE_SEEDS.map(trade => [
    trade.slug,
    { displayName: trade.displayName, isEnabled: true },
  ]),
);

const DEFAULT_TRADE_OPTIONS = INPUTS_TRADE_SEEDS.map(trade => ({
  value: trade.slug,
  label: trade.displayName,
}));

type ContractorContactInput = { vendorId: string; email: string; phoneNumber: string };

export const ContractorsPage = () => {
  let { id: contractorId } = useParams();
  const navigate = useNavigate();
  usePageTitle('Contractors');
  const [searchParams, setSearchParams] = useSearchParams();
  const showArchived = searchParams.get('archived') === 'true';
  const { contractors: activeContractors, contractorsMutators } = useOrgContractors({ archived: false });
  const { contractors: archivedContractors } = useOrgContractors({ archived: true });
  const { searchText, setSearchText, debouncedSearchText, clearSearch } = usePersistedSearch('contractors:search');
  const { order: sortOrder, orderBy: sortOrderBy, onSortChange } = usePersistedSort('contractors:sort', 'desc', 'createdDate');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { contractors: searchedContractors } = useOrgContractors({ archived: showArchived, search: debouncedSearchText || undefined });
  const baseContractors = showArchived ? archivedContractors : activeContractors;
  const hasActiveSearch = !!debouncedSearchText.trim();
  const contractors = hasActiveSearch ? (searchedContractors ?? baseContractors ?? []) : (baseContractors ?? []);
  const { markets } = useOrgMarkets();
  const { showToast, showErrorToast } = useToast();
  const { userCan } = useAuth();
  const { allOrgSettings } = useAllOrgSettings();
  const canCreateContractor = userCan('create', 'OrgContractor');
  const canUpdateContractor = userCan('update', 'OrgContractor');
  const { isError, error } = useOrgContractor(contractorId);

  const tradeDisplayNames = useMemo(
    () => {
      const configuredTrades = allOrgSettings?.tradeSettings?.trades;
      if (!configuredTrades || Object.keys(configuredTrades).length === 0) return DEFAULT_TRADE_DISPLAY_NAMES;
      return configuredTrades;
    },
    [allOrgSettings?.tradeSettings?.trades],
  );

  useAutoSwitchOrgForResource(
    { error: isError ? error : undefined, orgContractorId: contractorId },
  );

  useEffect(() => {
    if (isSearchExpanded) searchInputRef.current?.focus();
  }, [isSearchExpanded]);

  const contractorToEdit = contractors?.find(contractor => contractor.id === contractorId);
  const newContractor = contractorId === 'new';
  if (newContractor) contractorId = undefined;
  const flyoutOpen = newContractor || !!contractorToEdit;

  const tradeOptions = useMemo(() => {
    const configuredTrades = allOrgSettings?.tradeSettings?.trades;
    if (!configuredTrades || Object.keys(configuredTrades).length === 0) return DEFAULT_TRADE_OPTIONS;

    return Object.entries(configuredTrades)
      .filter(([, config]) => config.isEnabled !== false)
      .map(([value, config]) => ({
        value,
        label: config.displayName || value,
      }));
  }, [allOrgSettings?.tradeSettings?.trades]);

  if (!baseContractors) return <GenericLoadingPage />;

  const closeFlyout = () => {
    navigate('/contractors');
  };

  const addContractor = async (name: string, orgMarketIds: string[], trades: string[], { vendorId, email, phoneNumber }: ContractorContactInput) => {
    const newContractor = await contractorsMutators.create({
      name,
      trades,
      email: email.trim() || undefined,
      phoneNumber: phoneNumber.trim() || undefined,
      customerMetadata: vendorId ? { vendorId } : undefined,
    });
    await Promise.all(orgMarketIds.map(orgMarketId => contractorsMutators.addMarket(newContractor.id, orgMarketId)));
    trackEvent(ANALYTICS_EVENTS.contractorCreated, {
      contractorId: newContractor.id,
      name: newContractor.name,
      marketCount: orgMarketIds.length,
      tradesCount: trades.length,
    });
    closeFlyout();
  };

  const updateContractor = async (
    contractorId: string,
    name: string,
    orgMarketIds: string[],
    trades: string[],
    { vendorId, email, phoneNumber }: ContractorContactInput,
  ) => {
    const existingMarketIds = contractorToEdit?.contractorMarkets?.map(m => m.orgMarket.id) || [];
    const marketsToAdd = orgMarketIds.filter(id => !existingMarketIds.includes(id));
    const marketsToRemove = existingMarketIds.filter(id => !orgMarketIds.includes(id));
    await Promise.all([
      ...marketsToAdd.map(orgMarketId => contractorsMutators.addMarket(contractorId, orgMarketId)),
      ...marketsToRemove.map(orgMarketId => contractorsMutators.removeMarket(contractorId, orgMarketId)),
    ]);
    await contractorsMutators.update(contractorId, {
      name,
      trades,
      // null clears the field; vendorId keeps its original metadata behavior.
      email: email.trim() || null,
      phoneNumber: phoneNumber.trim() || null,
      customerMetadata: vendorId ? { vendorId } : undefined,
    });

    trackEvent(ANALYTICS_EVENTS.contractorUpdated, {
      contractorId,
      name,
      marketCount: orgMarketIds.length,
      tradesCount: trades.length,
    });

    closeFlyout();
  };

  const displayContractors = contractors
    .map((contractor: ApiOrgContractor) => ({
      id: contractor.id,
      name: contractor.name,
      vendorId: contractor.customerMetadata?.vendorId || '',
      email: contractor.email || '',
      phoneNumber: contractor.phoneNumber || '',
      markets: contractor.contractorMarkets?.map(m => m.orgMarket.name).join(', ') || '',
      trades: contractor.trades?.map(trade => getTradeLabel(trade, tradeDisplayNames)).join(', ') || '',
      createdDate: contractor.createdAt,
      updatedDate: contractor.updatedAt,
      link: `/contractor/${contractor.id}`,
      menu: [
        {
          label: canUpdateContractor ? 'Edit Contractor Details' : 'View Contractor Details',
          action: () => {
            navigate(`/contractor/${contractor.id}`);
          },
        },
        ...(canUpdateContractor
          ? [{
            label: contractor.archivedAt ? 'Unarchive' : 'Archive',
            action: async () => {
              const isArchiving = !contractor.archivedAt;
              await contractorsMutators.update(contractor.id, {
                archivedAt: contractor.archivedAt ? null : new Date().toISOString(),
              });
              trackEvent(isArchiving ? ANALYTICS_EVENTS.contractorArchived : ANALYTICS_EVENTS.contractorUnarchived, {
                contractorId: contractor.id,
                name: contractor.name,
              });
            },
          }]
          : []),
      ],
    }));

  const haveContractors = displayContractors.length > 0;
  const hasActiveSearchInput = !!searchText.trim();
  const shouldShowExpandedSearch = isSearchExpanded || !!searchText.trim();

  return (
    <PageLayout
      title={'Contractors' + (showArchived ? ' (archived)' : '')}
      contentLayoutStyling={'standard'}
    >
      <RecordListHeading
        recordType='Contractor'
        activeCount={activeContractors?.length ?? 0}
        archivedCount={archivedContractors?.length ?? 0}
        showArchived={showArchived}
        displayCountOverride={hasActiveSearchInput ? displayContractors.length : undefined}
        actionNodes={[
          shouldShowExpandedSearch
            ? (
              <div key='contractorSearch' css={SEARCH_BAR_STYLE}>
                <button
                  css={SEARCH_ICON_BUTTON_STYLE}
                  type='button'
                  onClick={() => {
                    if (!searchText.trim()) setIsSearchExpanded(false);
                  }}
                  aria-label='Search'
                >
                  <img src={searchIcon} css={SEARCH_ICON_STYLE} alt='Search' />
                </button>
                <input
                  ref={searchInputRef}
                  css={SEARCH_INPUT_STYLE}
                  type='search'
                  value={searchText}
                  onChange={event => setSearchText(event.target.value)}
                  placeholder='Search by contractor or trade'
                  aria-label='Search contractors'
                />
                {!!searchText.trim() && (
                  <button
                    css={SEARCH_CLOSE_BUTTON_STYLE}
                    type='button'
                    onClick={() => {
                      clearSearch();
                      setIsSearchExpanded(false);
                    }}
                    aria-label='Clear and close contractor search'
                  />
                )}
              </div>
            )
            : (
              <button
                key='contractorSearchCollapsed'
                css={SEARCH_COLLAPSED_BUTTON_STYLE}
                type='button'
                onClick={() => setIsSearchExpanded(true)}
                aria-label='Open contractor search'
              >
                <img src={searchIcon} css={SEARCH_ICON_STYLE} alt='Search' />
              </button>
            ),
        ]}
        onCreate={canCreateContractor ? () => navigate('/contractor/new') : undefined}
        onToggleArchived={() => {
          const newParams = new URLSearchParams(searchParams);
          if (showArchived) {
            newParams.delete('archived');
          } else {
            newParams.set('archived', 'true');
          }
          setSearchParams(newParams);
        }}
      />
      {!haveContractors
        ? (
          <NoRecordsPlaceholder
            recordType='Contractors'
            qualifier={showArchived ? 'Archived' : undefined}
            description={hasActiveSearchInput
              ? 'No contractors found. Try a different name or trade.'
              : canCreateContractor
              ? 'Create your first contractor to get started.'
              : 'No contractors have been created yet.'}
            createButtonText={hasActiveSearchInput ? 'Clear Search' : canCreateContractor ? 'Add a Contractor' : undefined}
            createFirstRecord={hasActiveSearchInput
              ? (() => {
                clearSearch();
                setIsSearchExpanded(false);
              })
              : canCreateContractor
              ? (() => navigate('/contractor/new'))
              : undefined}
            iconType='contractors'
          />
        )
        : (
          <DataTable
            recordType='contractor'
            items={displayContractors}
            defaultOrder={sortOrder}
            defaultOrderBy={sortOrderBy}
            onSortChange={onSortChange}
            columns={[
              {
                name: 'createdDate',
                sortable: true,
                sortMethod: 'date',
                width: LAYOUT2.spacing12,
                format: (value: string) => new Date(value).toLocaleDateString(),
              },
              {
                name: 'updatedDate',
                sortable: true,
                sortMethod: 'date',
                width: LAYOUT2.spacing12,
                format: (value: string) => new Date(value).toLocaleDateString(),
              },
              { name: 'name', sortable: true, width: LAYOUT2.spacing20 },
              { name: 'email', sortable: false, width: LAYOUT2.spacing20 },
              { name: 'phoneNumber', sortable: false, width: LAYOUT2.spacing16 },
              { name: 'vendorId', sortable: false, width: LAYOUT2.spacing12 },
              { name: 'markets', sortable: false, width: LAYOUT2.spacing20 },
              { name: 'trades', sortable: false },
            ]}
          />
        )}
      <FlyOut
        open={flyoutOpen}
        flyOutClosed={closeFlyout}
        title={!canUpdateContractor && contractorToEdit ? 'Contractor Details' : contractorToEdit ? 'Edit Contractor Details' : 'Add a Contractor'}
        subheader={canUpdateContractor ? <FormFieldsRequirementNote variant='optional' /> : undefined}
      >
        <Formik<{ name: string; orgMarketIds: string[]; trades: string[]; vendorId: string; email: string; phoneNumber: string }>
          initialValues={{
            name: contractorToEdit?.name || '',
            orgMarketIds: contractorToEdit?.contractorMarkets?.map(m => m.orgMarket.id) || [],
            trades: contractorToEdit?.trades || [],
            vendorId: contractorToEdit?.customerMetadata?.vendorId || '',
            email: contractorToEdit?.email || '',
            phoneNumber: contractorToEdit?.phoneNumber || '',
          }}
          validateOnMount
          validate={values => {
            const errors: Partial<Record<keyof typeof values, string>> = {};
            if (!values.name?.trim()) errors.name = 'Name is required';
            if (!values.trades || values.trades.length === 0) errors.trades = 'At least one trade is required';
            if (markets && markets.length > 0 && (!values.orgMarketIds || values.orgMarketIds.length === 0)) {errors.orgMarketIds =
                'At least one market is required';}
            return errors;
          }}
          enableReinitialize
          onSubmit={async values => {
            try {
              const contact = { vendorId: values.vendorId, email: values.email, phoneNumber: values.phoneNumber };
              if (contractorToEdit) {
                await updateContractor(contractorToEdit.id, values.name, values.orgMarketIds, values.trades, contact);
                showToast('Contractor updated successfully', 'success');
              } else {
                await addContractor(values.name, values.orgMarketIds, values.trades, contact);
                showToast('Contractor created successfully', 'success');
              }
            } catch (error) {
              showErrorToast(error, contractorToEdit ? 'update a contractor' : 'create a contractor');
            }
          }}
        >
          {({ handleSubmit, values, isValid, isSubmitting, errors, touched }) => {
            const creatingContractor = !contractorToEdit;
            const updatingContractor = !!contractorToEdit;
            const readOnly = (updatingContractor && !canUpdateContractor) || (creatingContractor && !canCreateContractor);
            return (
              <StyledForm onSubmit={handleSubmit}>
                <FormikLabeledInput
                  name='name'
                  label='Name'
                  placeholder='Enter a contractor name'
                  required
                  disabled={readOnly}
                />
                <FormikLabeledInput
                  name='email'
                  label='Email'
                  placeholder='Enter a contact email'
                  optional
                  disabled={readOnly}
                />
                <FormikLabeledInput
                  name='phoneNumber'
                  label='Phone Number'
                  placeholder='Enter a contact phone number'
                  optional
                  disabled={readOnly}
                />
                <FormikLabeledInput
                  name='vendorId'
                  label='Vendor ID'
                  placeholder='Enter vendor ID number'
                  optional
                  disabled={readOnly}
                />
                <FormikLabeledMultiSelect
                  name='trades'
                  label='Trades'
                  placeholder='Add trades...'
                  items={tradeOptions}
                  disabled={readOnly}
                />
                {markets
                  && (
                    <FormikLabeledMultiSelect
                      name='orgMarketIds'
                      label='Markets'
                      placeholder='Add markets...'
                      items={markets.map(m => ({ value: m.id, label: m.name }))}
                      disabled={readOnly}
                    />
                  )}
                {!readOnly && (
                  <Button
                    color='green'
                    disabled={!isValid || isSubmitting}
                    type='submit'
                  >
                    {updatingContractor ? 'Update Contractor' : 'Add Contractor'}
                  </Button>
                )}
              </StyledForm>
            );
          }}
        </Formik>
      </FlyOut>
    </PageLayout>
  );
};
